"use client";

import { Fragment, ReactNode } from "react";
import Link from "next/link";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/shadcn/breadcrumb";
import { cn } from "@/lib/utils";

export interface IBreadcrumb {
  label: string;
  // Only where the crumb leads to another page: a group of sections, or the
  // page you are on, is named without a link.
  href?: string;
  icon?: ReactNode;
}

interface PageHeaderProps {
  title?: string | ReactNode;
  breadcrumbs?: IBreadcrumb[];
  actions?: ReactNode;
  className?: string;
  // Page-level controls that sit between the title and the actions (a page's own tabs).
  children?: ReactNode;
}

export const PageHeader = ({
  title,
  breadcrumbs,
  actions,
  className,
  children,
}: PageHeaderProps) => (
  <header
    className={cn(
      "box-content flex items-center justify-between gap-4 px-4 py-2 h-8 border-b border-border bg-background shrink-0",
      className
    )}
  >
    <div className="flex items-center gap-2 min-w-0 shrink-0 max-w-[50%]">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb className="shrink-0">
          <BreadcrumbList className="gap-2 sm:gap-2">
            {breadcrumbs.map((crumb) => (
              <Fragment key={crumb.href ?? crumb.label}>
                <BreadcrumbItem>
                  {crumb.href ? (
                    <BreadcrumbLink asChild>
                      <Link
                        href={crumb.href}
                        className="flex items-center gap-2 whitespace-nowrap"
                      >
                        {crumb.icon}
                        {crumb.label}
                      </Link>
                    </BreadcrumbLink>
                  ) : (
                    <span className="flex cursor-default items-center gap-2 truncate whitespace-nowrap">
                      {crumb.icon}
                      {crumb.label}
                    </span>
                  )}
                </BreadcrumbItem>
                <BreadcrumbSeparator className="flex items-center text-sm leading-none text-muted-foreground">
                  /
                </BreadcrumbSeparator>
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      )}
      {title &&
        (typeof title === "string" ? (
          <h1 className="text-sm font-medium truncate">{title}</h1>
        ) : (
          <div className="min-w-0">{title}</div>
        ))}
    </div>
    {children && <div className="flex min-w-0 flex-1">{children}</div>}
    {actions && (
      <div className="flex items-center shrink-0 ml-auto">{actions}</div>
    )}
  </header>
);
